/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Col, Modal, Pagination, Row, Table, Typography } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  RightCircleOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { deleteAdminSpare } from '../../redux/admin/sparesAdmin/action';
import { Button as CrcButton, Spinner } from '@crc-evans/ui-components';
import '../../pages/technical/HeatProfilePage.css';
import { useHeatStyleColumnFacets } from '../technical/heatStyleColumnFacets';
import { ArrowLeft } from 'lucide-react';

const { Text } = Typography;

const SPARES_FACET_KEYS = [
  'equipmentType',
  'subCategory',
  'sapSparesKitNumber',
  'partSapNumber',
  'description',
  'sparesKitQty',
  'safteyStock',
  'unitCost',
  'refurbKitQty',
  'binLocation',
  'supplierPart',
  'preferredSupplier',
  'notes',
];

const SPARES_COLUMN_FILTERS_INITIAL = SPARES_FACET_KEYS.reduce(
  (acc, k) => ({ ...acc, [k]: '' }),
  {}
);

/** Cap rows scanned for facet distinct values (full list copy + scan was freezing the UI). */
const SPARES_FACET_SOURCE_CAP = 12000;

/** Native `title` tooltips only — Ant Design Tooltip clones the trigger when open, which can show a duplicate icon in fixed table cells. */
const SparesActionsCell = React.memo(function SparesActionsCell({ record, onEdit, onRequestDelete }) {
  return (
    <div className="heat-profile-actions-cell spares-list-actions-cell">
      <span
        className="heat-profile-actions-tooltip-trigger"
        title="Update"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit(record);
          }
        }}
      >
        <EditOutlined
          className="txtIcon"
          style={{ marginRight: '8px' }}
          onClick={() => onEdit(record)}
        />
      </span>
      <span
        className="heat-profile-actions-tooltip-trigger"
        title="Delete"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRequestDelete(record);
          }
        }}
      >
        <DeleteOutlined
          className="txtIcon"
          onClick={() => onRequestDelete(record)}
        />
      </span>
    </div>
  );
});

const SparesList = (props) => {
  const {
    setOpenModel,
    equipTypeList,
    sparesAdminList,
    adminEquipTypesLoading,
    spareTypeForm,
    isSparesListLoading,
    setCurrency,
    setEditSpareId,
    onGetFetchSparesAdminList,
    sparesDataCount,

    setEditModelValue,
    setEditRecord,
    onDeleteAdminSpare,
    isEquipTypeDeleteLoading,
    equipTypeSearchForm,
  } = props;

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteEquipId, setDeleteEquipId] = useState('');
  const [current, setCurrent] = useState(1);
  const [currentPagesize, setCurrentPagesize] = useState(20);
  const [columnFilters, setColumnFilters] = useState(() => ({ ...SPARES_COLUMN_FILTERS_INITIAL }));

  const [sortKey, setSortKey] = useState('equipmentType');
  const [sortDesc, setSortDesc] = useState(false);

  // Stepped navigation state (like Asset Management)
  const [step, setStep] = useState(1);
  const [selectedEquipmentType, setSelectedEquipmentType] = useState('');
  const [selectedSapKitNumber, setSelectedSapKitNumber] = useState('');

  const handleSortClick = useCallback((field) => {
    setSortKey((prev) => {
      if (prev === field) {
        setSortDesc((d) => !d);
        return prev;
      }
      setSortDesc(false);
      return field;
    });
  }, []);

  const handleColumnFilterChange = useCallback((key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrent(1);
  }, []);

  const sparesListFacetRef = useRef([]);
  sparesListFacetRef.current = sparesAdminList || [];
  const fetchSparesFacetRows = useCallback(async () => {
    const list = sparesListFacetRef.current;
    if (!Array.isArray(list) || list.length === 0) return [];
    return list.length <= SPARES_FACET_SOURCE_CAP ? list : list.slice(0, SPARES_FACET_SOURCE_CAP);
  }, []);

  const searchCriteriaForSparesFacets = useMemo(() => ({}), []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } =
    useHeatStyleColumnFacets({
      enabled: true,
      filterableColumns: SPARES_FACET_KEYS,
      listCount: (sparesAdminList || []).length,
      projectList: sparesAdminList || [],
      searchCriteria: searchCriteriaForSparesFacets,
      columnFilters,
      onColumnFilterChange: handleColumnFilterChange,
      fetchFacetRows: fetchSparesFacetRows,
    });

  const sparesSearchDebounceRef = useRef(null);
  const skipInitialSparesSearchFetch = useRef(true);
  useEffect(() => {
    if (!onGetFetchSparesAdminList) return;
    if (sparesSearchDebounceRef.current) clearTimeout(sparesSearchDebounceRef.current);
    sparesSearchDebounceRef.current = setTimeout(() => {
      if (skipInitialSparesSearchFetch.current) {
        skipInitialSparesSearchFetch.current = false;
        return;
      }
      const payload = { pageNumber: 1, pageSize: 100000 };
      SPARES_FACET_KEYS.forEach((k) => {
        const v = String(columnFilters[k] ?? '').trim();
        if (v) payload[k] = v;
      });
      onGetFetchSparesAdminList(payload);
    }, 550);
    return () => {
      if (sparesSearchDebounceRef.current) clearTimeout(sparesSearchDebounceRef.current);
    };
  }, [columnFilters, onGetFetchSparesAdminList]);

  const facetFilteredSpares = useMemo(
    () => applyFacetClientFilter([...(sparesAdminList || [])]),
    [sparesAdminList, applyFacetClientFilter]
  );

  // Helper groupings and sort data sources for the 3 steps

  // Step 1: Unique Equipment Types
  const step1DataSource = useMemo(() => {
    const groups = {};
    facetFilteredSpares.forEach((item) => {
      const eq = item.equipmentType || item.sparesEquipmentType || '';
      if (!eq) return;
      if (!groups[eq]) {
        groups[eq] = {
          equipmentType: eq,
        };
      }
    });

    const list = Object.values(groups).map((g) => ({
      equipmentType: g.equipmentType,
      key: g.equipmentType,
    }));

    // Sort list
    list.sort((a, b) => {
      const va = a.equipmentType;
      const vb = b.equipmentType;
      const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      return sortDesc ? -cmp : cmp;
    });

    return list;
  }, [facetFilteredSpares, sortDesc]);

  // Step 2: Unique SAP Kit Numbers for the selected Equipment Type
  const step2DataSource = useMemo(() => {
    const filtered = facetFilteredSpares.filter(
      (item) => (item.equipmentType || item.sparesEquipmentType) === selectedEquipmentType
    );

    const groups = {};
    filtered.forEach((item) => {
      const kit = item.sapSparesKitNumber || '';
      if (!kit) return;
      if (!groups[kit]) {
        groups[kit] = {
          sapSparesKitNumber: kit,
          descriptionsSet: new Set(),
          sapNumbersSet: new Set(),
          sparesCount: 0,
        };
      }
      if (item.description) groups[kit].descriptionsSet.add(item.description);
      if (item.partSapNumber) groups[kit].sapNumbersSet.add(item.partSapNumber);
      groups[kit].sparesCount += 1;
    });

    const list = Object.values(groups).map((g) => ({
      sapSparesKitNumber: g.sapSparesKitNumber,
      description: Array.from(g.descriptionsSet)[0] || '',
      sapNumbersCount: g.sapNumbersSet.size,
      sparesCount: g.sparesCount,
      key: g.sapSparesKitNumber,
    }));

    // Sort list
    list.sort((a, b) => {
      if (sortKey === 'sparesCount') {
        const na = a.sparesCount;
        const nb = b.sparesCount;
        return sortDesc ? nb - na : na - nb;
      }
      if (sortKey === 'sapNumbersCount') {
        const na = a.sapNumbersCount;
        const nb = b.sapNumbersCount;
        return sortDesc ? nb - na : na - nb;
      }
      const va = (a[sortKey] ?? '').toString();
      const vb = (b[sortKey] ?? '').toString();
      const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      return sortDesc ? -cmp : cmp;
    });

    return list;
  }, [facetFilteredSpares, selectedEquipmentType, sortKey, sortDesc]);

  // Step 3: Raw Spares details for selected Equipment Type + SAP Kit Number
  const step3DataSource = useMemo(() => {
    const filtered = facetFilteredSpares.filter(
      (item) =>
        (item.equipmentType || item.sparesEquipmentType) === selectedEquipmentType &&
        item.sapSparesKitNumber === selectedSapKitNumber
    );

    // Sort spares details
    filtered.sort((a, b) => {
      if (sortKey === 'partSapNumber') {
        const va = (a.partSapNumber ?? '').toString();
        const vb = (b.partSapNumber ?? '').toString();
        const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
        return sortDesc ? -cmp : cmp;
      }
      if (sortKey === 'sparesKitQty') {
        const va = Number(a.sparesKitQty) || 0;
        const vb = Number(b.sparesKitQty) || 0;
        return sortDesc ? vb - va : va - vb;
      }
      if (sortKey === 'refurbKitQty') {
        const va = Number(a.refurbKitQty) || 0;
        const vb = Number(b.refurbKitQty) || 0;
        return sortDesc ? vb - va : va - vb;
      }
      if (sortKey === 'safteyStock') {
        const va = Number(a.safteyStock) || 0;
        const vb = Number(b.safteyStock) || 0;
        return sortDesc ? vb - va : va - vb;
      }
      if (sortKey === 'totalCost') {
        const ta = (Number(a.unitCost) || 0) * (Number(a.sparesKitQty) || 0);
        const tb = (Number(b.unitCost) || 0) * (Number(b.sparesKitQty) || 0);
        return sortDesc ? tb - ta : ta - tb;
      }
      if (sortKey === 'unitCost') {
        const va = parseFloat(String(a.unitCost ?? '').replace(/[^0-9.-]/g, '')) || 0;
        const vb = parseFloat(String(b.unitCost ?? '').replace(/[^0-9.-]/g, '')) || 0;
        return sortDesc ? vb - va : va - vb;
      }
      const va = (a[sortKey] ?? '').toString();
      const vb = (b[sortKey] ?? '').toString();
      const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      return sortDesc ? -cmp : cmp;
    });

    return filtered;
  }, [facetFilteredSpares, selectedEquipmentType, selectedSapKitNumber, sortKey, sortDesc]);

  // Current active data source based on step
  const currentDataSource = useMemo(() => {
    if (step === 1) return step1DataSource;
    if (step === 2) return step2DataSource;
    return step3DataSource;
  }, [step, step1DataSource, step2DataSource, step3DataSource]);

  /** Stable unique keys and displays S.No */
  const tableDataWithDisplaySno = useMemo(
    () =>
      currentDataSource.map((row, i) => ({
        ...row,
        _displaySno: i + 1,
        _tableRowKey: `spare-step-${step}-${i}-${row.id != null && String(row.id) !== '' ? String(row.id) : row.key || 'noid'}`,
        key: `spare-step-${step}-${i}-${row.id != null && String(row.id) !== '' ? String(row.id) : row.key || 'noid'}`,
      })),
    [currentDataSource, step]
  );

  const pagedTableData = useMemo(() => {
    const start = (current - 1) * currentPagesize;
    return tableDataWithDisplaySno.slice(start, start + currentPagesize);
  }, [tableDataWithDisplaySno, current, currentPagesize]);

  const renderSortCaret = useCallback(
    (field) =>
      sortKey === field ? (
        sortDesc ? (
          <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        ) : (
          <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        )
      ) : (
        <CaretUpOutlined
          className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
          aria-hidden
        />
      ),
    [sortKey, sortDesc]
  );

  const renderSortOnlyTitle = useCallback(
    (title, sortField) => (
      <div className="column-header-with-filter">
        <div className="column-header-text-wrapper spares-column-header-sortable">
          <span>{title}</span>
          <span
            className="custom-sort-icon"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleSortClick(sortField);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSortClick(sortField);
              }
            }}
          >
            {renderSortCaret(sortField)}
          </span>
        </div>
      </div>
    ),
    [handleSortClick, renderSortCaret]
  );

  const renderSparesColumnTitle = useCallback(
    (title, dataIndex, sortField) => (
      <div className="column-header-with-filter">
        <div className="column-header-text-wrapper spares-column-header-sortable">
          <span>{title}</span>
          <span
            className="custom-sort-icon"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleSortClick(sortField);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSortClick(sortField);
              }
            }}
          >
            {renderSortCaret(sortField)}
          </span>
          {renderTextColumnFilterTrigger(dataIndex)}
        </div>
        <div className="header-filter-input user-mgmt-header-search">
          <div
            className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell"
            style={dataIndex === 'equipmentType' || dataIndex === 'sapSparesKitNumber' ? { width: '50%' } : undefined}
          >
            <Input
              placeholder="Search"
              value={columnFilters[dataIndex] ?? ''}
              onChange={(e) => handleColumnFilterChange(dataIndex, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              size="small"
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>
    ),
    [
      columnFilters,
      handleColumnFilterChange,
      handleSortClick,
      renderSortCaret,
      renderTextColumnFilterTrigger,
    ]
  );

  const openEditSpare = useCallback(
    (record) => {
      setOpenModel(true);
      setEditRecord(record);
      setEditModelValue(true);
      spareTypeForm.setFieldsValue(record);
      setCurrency(record.currency);
      setEditSpareId(record.id);
    },
    [setOpenModel, setEditRecord, setEditModelValue, spareTypeForm, setCurrency, setEditSpareId]
  );

  const openDeleteConfirm = useCallback((record) => {
    setDeleteModal(true);
    setDeleteEquipId(record);
  }, []);

  // Columns definition for each step

  const step1Columns = useMemo(
    () => [
      {
        title: renderSortOnlyTitle('S.NO', 'serialNo'),
        dataIndex: 'serialNo',
        key: 'serialNo',
        align: 'center',
        width: 80,
        render: (text, record) => record._displaySno,
      },
      {
        title: renderSparesColumnTitle('EQUIPMENT TYPE', 'equipmentType', 'equipmentType'),
        dataIndex: 'equipmentType',
        key: 'equipmentType',
        align: 'left',
      },
      {
        title: 'ACTIONS',
        key: 'action',
        align: 'center',
        width: 120,
        render: (_, record) => (
          <div className="tableIcons" style={{ display: 'flex', justifyContent: 'center' }}>
            <span
              title="View SAP Kit Numbers"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedEquipmentType(record.equipmentType);
                  setStep(2);
                  setCurrent(1);
                }
              }}
            >
              <RightCircleOutlined
                className="equipment-type-action-icon"
                style={{ fontSize: '18px', cursor: 'pointer' }}
                onClick={() => {
                  setSelectedEquipmentType(record.equipmentType);
                  setStep(2);
                  setCurrent(1);
                }}
              />
            </span>
          </div>
        ),
      },
    ],
    [renderSortOnlyTitle, renderSparesColumnTitle]
  );

  const step2Columns = useMemo(
    () => [
      {
        title: renderSortOnlyTitle('S.NO', 'serialNo'),
        dataIndex: 'serialNo',
        key: 'serialNo',
        align: 'center',
        width: 80,
        render: (text, record) => record._displaySno,
      },
      {
        title: renderSparesColumnTitle('SAP KIT NO', 'sapSparesKitNumber', 'sapSparesKitNumber'),
        dataIndex: 'sapSparesKitNumber',
        key: 'sapSparesKitNumber',
        align: 'left',
      },
      {
        title: 'ACTIONS',
        key: 'action',
        align: 'center',
        width: 120,
        render: (_, record) => (
          <div className="tableIcons" style={{ display: 'flex', justifyContent: 'center' }}>
            <span
              title="View Spares"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedSapKitNumber(record.sapSparesKitNumber);
                  setStep(3);
                  setCurrent(1);
                }
              }}
            >
              <RightCircleOutlined
                className="equipment-type-action-icon"
                style={{ fontSize: '18px', cursor: 'pointer' }}
                onClick={() => {
                  setSelectedSapKitNumber(record.sapSparesKitNumber);
                  setStep(3);
                  setCurrent(1);
                }}
              />
            </span>
          </div>
        ),
      },
    ],
    [renderSortOnlyTitle, renderSparesColumnTitle]
  );

  const step3Columns = useMemo(
    () => [
      {
        title: renderSortOnlyTitle('S.NO', 'serialNo'),
        dataIndex: 'serialNo',
        key: 'serialNo',
        align: 'center',
        width: 80,
        render: (text, record) => record._displaySno,
      },
      {
        title: renderSparesColumnTitle('SAP NUMBER', 'partSapNumber', 'partSapNumber'),
        dataIndex: 'partSapNumber',
        key: 'partSapNumber',
        align: 'left',
        width: 150,
      },
      {
        title: renderSparesColumnTitle('SUB CATEGORY', 'subCategory', 'subCategory'),
        dataIndex: 'subCategory',
        key: 'subCategory',
        align: 'left',
        width: 200,
      },
      {
        title: renderSparesColumnTitle('DESCRIPTION', 'description', 'description'),
        dataIndex: 'description',
        key: 'description',
        align: 'left',
        width: 300,
        render: (text) => (
          <div className="description-cell">
            {text && text.length > 50 ? text.slice(0, 50) + '...' : text}
          </div>
        ),
      },
      {
        title: renderSparesColumnTitle('SPARES KIT QTY', 'sparesKitQty', 'sparesKitQty'),
        dataIndex: 'sparesKitQty',
        key: 'sparesKitQty',
        align: 'center',
        width: 150,
      },
      {
        title: renderSparesColumnTitle('SAFETY STOCK', 'safteyStock', 'safteyStock'),
        dataIndex: 'safteyStock',
        key: 'safteyStock',
        align: 'center',
        width: 150,
        render: (text) => <div style={{ textAlign: 'center' }}>{text}</div>,
      },
      {
        title: renderSparesColumnTitle('UNIT COST', 'unitCost', 'unitCost'),
        dataIndex: 'unitCost',
        key: 'unitCost',
        align: 'left',
        width: 150,
        render: (text, record) => (
          <div className="description-cell">
            {record.currency === 'dollar' ? '$' : record.currency === 'pound' ? '£' : '€'} {text}
          </div>
        ),
      },
      {
        title: renderSparesColumnTitle('REFURB KIT QTY', 'refurbKitQty', 'refurbKitQty'),
        dataIndex: 'refurbKitQty',
        key: 'refurbKitQty',
        align: 'center',
        width: 150,
        render: (text) => <div style={{ textAlign: 'center' }}>{text ?? ''}</div>,
      },
      {
        title: renderSparesColumnTitle('BIN LOCATION', 'binLocation', 'binLocation'),
        dataIndex: 'binLocation',
        key: 'binLocation',
        align: 'left',
        width: 160,
      },
      {
        title: renderSparesColumnTitle('SUPPLIER PART', 'supplierPart', 'supplierPart'),
        dataIndex: 'supplierPart',
        key: 'supplierPart',
        align: 'left',
        width: 170,
      },
      {
        title: renderSparesColumnTitle('PREFERRED SUPPLIER', 'preferredSupplier', 'preferredSupplier'),
        dataIndex: 'preferredSupplier',
        key: 'preferredSupplier',
        align: 'left',
        width: 200,
      },
      {
        title: renderSparesColumnTitle('NOTES', 'notes', 'notes'),
        dataIndex: 'notes',
        key: 'notes',
        align: 'left',
        width: 220,
        render: (text) => (
          <div className="description-cell">
            {text && String(text).length > 40 ? String(text).slice(0, 40) + '...' : text || ''}
          </div>
        ),
      },
      {
        title: renderSortOnlyTitle('TOTAL COST', 'totalCost'),
        dataIndex: 'totalCost',
        key: 'totalCost',
        align: 'center',
        width: 150,
        render: (text, record) => {
          const unitCostVal = record.unitCost;
          const quantity = record.sparesKitQty;
          const totalCost = (unitCostVal || 0) * (quantity || 0);
          const totalCostRounded = totalCost.toFixed(2);
          return (
            <span>
              {record.currency === 'dollar' ? '$' : record.currency === 'pound' ? '£' : '€'}{' '}
              {totalCostRounded}
            </span>
          );
        },
      },
      {
        title: 'ACTIONS',
        key: 'action',
        dataIndex: 'action',
        align: 'center',
        width: 130,
        fixed: 'right',
        render: (text, record) => (
          <SparesActionsCell
            record={record}
            onEdit={openEditSpare}
            onRequestDelete={openDeleteConfirm}
          />
        ),
      },
    ],
    [renderSortOnlyTitle, renderSparesColumnTitle, openEditSpare, openDeleteConfirm]
  );

  const typesListColumns = useMemo(() => {
    if (step === 1) return step1Columns;
    if (step === 2) return step2Columns;
    return step3Columns;
  }, [step, step1Columns, step2Columns, step3Columns]);

  const scrollConfig = useMemo(() => {
    if (step === 1) return undefined;
    if (step === 2) return undefined;
    return { x: 2500 };
  }, [step]);

  /** Resetting values on Closing Modal */
  const handleCancel = () => {
    setDeleteModal(false);
    setDeleteEquipId('');
  };

  /** Waiting to delete equipment type and close Modal */
  useEffect(() => {
    if (!isSparesListLoading) {
      setDeleteModal(false);
      setDeleteEquipId('');
    }
  }, [isSparesListLoading]);

  useEffect(() => {
    setCurrent(1);
  }, [sortKey, sortDesc, step]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(currentDataSource.length / currentPagesize) || 1);
    if (current > maxPage) {
      setCurrent(maxPage);
    }
  }, [currentDataSource.length, currentPagesize, current]);

  const renderHeader = () => {
    if (step === 1) return null;

    let titleText = '';
    if (step === 2) {
      titleText = `Equipment: ${selectedEquipmentType}`;
    } else if (step === 3) {
      titleText = `Equipment: ${selectedEquipmentType} > SAP Kit No: ${selectedSapKitNumber}`;
    }

    return (
      <div
        className="back_equip back_equip-modern"
        style={{ padding: '0 0 20px 0', borderBottom: '1px solid #e2e8f0', marginBottom: '24px', marginTop: '0px' }}
      >
        <div
          className="back_equip-modern-inner"
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '16px' }}
        >
          <span
            title="Back"
            role="button"
            tabIndex={0}
            onClick={() => {
              if (step === 2) {
                setStep(1);
                setSelectedEquipmentType('');
              } else if (step === 3) {
                setStep(2);
                setSelectedSapKitNumber('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (step === 2) {
                  setStep(1);
                  setSelectedEquipmentType('');
                } else if (step === 3) {
                  setStep(2);
                  setSelectedSapKitNumber('');
                }
              }
            }}
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <ArrowLeft
              size={18}
              strokeWidth={2.5}
              style={{ cursor: 'pointer', transition: 'color 0.2s', color: '#64748b' }}
              className="equipment-type-action-icon"
            />
          </span>
          <div
            className="back_equip-modern-title"
            style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}
          >
            {titleText}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Row align="top" className="spares-list-page-row" gutter={0}>
        <Col
          span={24}
          style={{ padding: '0' }}
        >
          {/*
            pipeline-table padding must wrap both Table + external Pagination, otherwise
            the pager sits outside the padded “white” block and appears on the grey page bg.
          */}
          <div className="spares-list-table-pager-wrap pipeline-table">
            {renderHeader()}
            <Table
              className="heat-profile-table spares-list-table"
              columns={typesListColumns}
              dataSource={pagedTableData}
              rowKey={(record) => record._tableRowKey}
              tableLayout="fixed"
              loading={{
                spinning: isSparesListLoading,
                indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
              }}
              pagination={false}
              scroll={scrollConfig}
            />
            {currentDataSource.length > 0 && (
              <div className="spares-list-pager-outer">
                <Pagination
                  className="heat-profile-pagination"
                  current={current}
                  pageSize={currentPagesize}
                  total={currentDataSource.length}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  onChange={(page, size) => {
                    setCurrent(page);
                    if (size != null) {
                      setCurrentPagesize(size);
                    }
                  }}
                  showTotal={(total, range) => `${range[0]} - ${range[1]} of ${total} items`}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Modal
        width={500}
        className="heatprofile-modal"
        title="Delete Spare"
        visible={deleteModal}
        style={{ top: 100 }}
        onCancel={handleCancel}
        footer={[
          <CrcButton
            key="cancel"
            label="Cancel"
            variant="secondary"
            onClick={handleCancel}
            size="medium"
            disabled={isSparesListLoading}
          />,
          <CrcButton
            key="apply"
            label={isSparesListLoading ? 'Deleting...' : 'Confirm Delete'}
            variant="primary"
            onClick={() => onDeleteAdminSpare({ id: deleteEquipId.id })}
            size="medium"
            disabled={isSparesListLoading}
          />,
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text style={{ fontSize: '16px', color: '#374151' }}>
            Are you sure you want to delete spare?
          </Text>
          <div style={{ marginTop: '12px', fontWeight: 'bold', color: '#ef314c', fontSize: '18px' }}>
            {deleteEquipId && `${deleteEquipId.equipmentType} - ${deleteEquipId.subCategory}`}
          </div>
          <p style={{ marginTop: '16px', color: '#6b7280' }}>
            This action cannot be undone and will remove the spare from the database.
          </p>
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = ({ spareAdminReducer }) => {
  const isSparesListLoading = get(spareAdminReducer, 'isFetchsparesAdminDataLoading', []);
  const sparesDataCount = get(spareAdminReducer, 'sparesDataCount', []);
  return {
    isSparesListLoading,
    sparesDataCount,
  };
};

const mapDispatchToProps = {
  onDeleteAdminSpare: deleteAdminSpare,
};

export default connect(mapStateToProps, mapDispatchToProps)(SparesList);


